import {
  encodeMultiSelectCellValue,
  encodeSelectCellValue,
  normalizeSelectOptions,
  selectDisplayColor,
  selectDisplayLabel,
  type SelectOption,
} from '@/lib/databases/values';
import { anchorBelowElement, Menu } from '@/components/ui/Menu.web';
import { createElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Pressable } from 'react-native';

import type { BlockPropertyRecord } from '@/lib/databases/types';
import type { DatabaseColumnDefinition } from '@/lib/databases/types';

import { SelectChip } from './SelectChip.web';

function payloadIds(property: BlockPropertyRecord | undefined, multi: boolean): string[] {
  const v = property?.value;
  if (!v || typeof v !== 'object' || !('t' in v)) {
    return [];
  }
  const raw = (v as { v?: unknown }).v;
  if (multi) {
    return Array.isArray(raw) ? raw.map(String).filter(Boolean) : [];
  }
  return raw != null && String(raw) !== '' ? [String(raw)] : [];
}

type SelectCellEditorProps = {
  column: DatabaseColumnDefinition;
  property: BlockPropertyRecord | undefined;
  disabled?: boolean;
  saving?: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSave: (value: Record<string, unknown> | null) => Promise<void>;
  onEnsureOption?: (name: string) => Promise<SelectOption>;
  variant?: 'table' | 'panel';
};

export function SelectCellEditor({
  column,
  property,
  disabled,
  saving,
  isOpen,
  onOpen,
  onClose,
  onSave,
  onEnsureOption,
  variant = 'table',
}: SelectCellEditorProps) {
  const multi = column.type === 'multi_select';
  const options = normalizeSelectOptions(column.config);
  const selectedIds = payloadIds(property, multi);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const panelMode = variant === 'panel';

  function setRootEl(el: HTMLDivElement | null) {
    rootRef.current = el;
    setAnchorEl(el);
  }

  useLayoutEffect(() => {
    if (isOpen && rootRef.current) {
      setAnchorEl(rootRef.current);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  const filtered = options.filter((o) =>
    o.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const queryTrim = query.trim();
  const canCreate =
    queryTrim.length > 0 &&
    !options.some((o) => o.name.toLowerCase() === queryTrim.toLowerCase());

  async function commitSingle(optionId: string | null) {
    const encoded = encodeSelectCellValue(optionId);
    await onSave(encoded as Record<string, unknown> | null);
    onClose();
  }

  async function commitMulti(nextIds: string[]) {
    const encoded = encodeMultiSelectCellValue(nextIds);
    await onSave(encoded as Record<string, unknown> | null);
  }

  async function pickOption(opt: SelectOption) {
    if (disabled || saving) {
      return;
    }
    if (multi) {
      const has = selectedIds.includes(opt.id);
      const next = has ? selectedIds.filter((id) => id !== opt.id) : [...selectedIds, opt.id];
      await commitMulti(next);
      return;
    }
    await commitSingle(opt.id);
  }

  async function createAndPick() {
    if (!onEnsureOption || !queryTrim || disabled || saving) {
      return;
    }
    const opt = await onEnsureOption(queryTrim);
    setQuery('');
    await pickOption(opt);
  }

  function toggleOpen() {
    if (disabled || saving) {
      return;
    }
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }

  const chips = selectedIds
    .map((id) => {
      const label = selectDisplayLabel(column.config, id);
      if (!label) {
        return null;
      }
      return {
        id,
        label,
        color: selectDisplayColor(column.config, id),
      };
    })
    .filter((c): c is { id: string; label: string; color: ReturnType<typeof selectDisplayColor> } =>
      c !== null,
    );

  const popoverContent = isOpen
      ? [
          createElement('input', {
            type: 'text',
            className: 'cs-db-select-search',
            placeholder: multi ? 'Select options…' : 'Select an option…',
            value: query,
            autoFocus: true,
            disabled: disabled || saving,
            onChange: (e: Event) => setQuery((e.target as HTMLInputElement).value),
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Escape') {
                onClose();
              }
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                void createAndPick();
              }
              e.stopPropagation();
            },
          }),
          createElement(
            'div',
            { className: 'cs-db-select-options' },
            filtered.map((opt) =>
              createElement(
                'button',
                {
                  key: opt.id,
                  type: 'button',
                  className: `cs-db-select-option${
                    selectedIds.includes(opt.id) ? ' cs-db-select-option-selected' : ''
                  }`,
                  disabled: disabled || saving,
                  onClick: () => void pickOption(opt),
                },
                createElement(SelectChip, { label: opt.name, color: opt.color }),
              ),
            ),
            canCreate
              ? createElement(
                  'button',
                  {
                    type: 'button',
                    className: 'cs-db-select-create',
                    disabled: disabled || saving || !onEnsureOption,
                    onClick: () => void createAndPick(),
                  },
                  `Create "${queryTrim}"`,
                )
              : null,
            !multi
              ? createElement(
                  'button',
                  {
                    type: 'button',
                    className: 'cs-db-select-clear',
                    disabled: disabled || saving,
                    onClick: () => void commitSingle(null),
                  },
                  'Clear',
                )
              : null,
          ),
        ]
      : null;

  const popoverInner =
    popoverContent &&
    createElement('div', { className: 'cs-db-select-popover' }, ...popoverContent);

  const tableMenu =
    !panelMode &&
    createElement(Menu, {
      open: isOpen,
      anchor: anchorEl ? anchorBelowElement(anchorEl) : null,
      onClose,
      dataAttribute: 'cs-select-editor',
      minWidth: 260,
      children: isOpen ? popoverInner : null,
    });

  const cellContent = createElement(
    'div',
    {
      className: panelMode
        ? `cs-db-select-cell cs-db-select-cell-panel${isOpen ? ' cs-db-select-cell-panel--open' : ''}`
        : 'cs-db-select-cell cs-db-select-cell--table',
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
          toggleOpen();
        }
      },
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
    },
    chips.length > 0
      ? chips.map((c) =>
          createElement(SelectChip, {
            key: c.id,
            label: c.label,
            color: c.color,
            onRemove:
              multi && !disabled && isOpen
                ? () => {
                    void commitMulti(selectedIds.filter((id) => id !== c.id));
                  }
                : undefined,
          }),
        )
      : !isOpen
        ? createElement(
            'span',
            { className: 'cs-db-select-placeholder' },
            multi ? 'Select options…' : 'Select…',
          )
        : null,
    panelMode && isOpen ? popoverInner : null,
  );

  if (panelMode) {
    return createElement(
      'div',
      { ref: setRootEl, 'data-cs-select-editor': 'true', className: 'cs-db-select-editor-root' },
      cellContent,
    );
  }

  return createElement(
    'div',
    { ref: setRootEl, 'data-cs-select-editor': 'true', className: 'cs-db-select-editor-root' },
    createElement(
      Pressable,
      {
        onPress: toggleOpen,
        className:
          'flex h-full min-h-[32px] w-full min-w-0 max-w-full overflow-hidden items-center justify-start px-2 py-1',
      },
      cellContent,
    ),
    tableMenu,
  );
}
